import { create } from 'zustand';
import { clientShoppingListsManagementApi } from '@api/api-config.ts';
import { ClientShoppingListSummaryDTO } from '@api/generated/search-service';
import { useUser } from '@hooks/useUser.ts';
import { useToastStore } from '@stores/toastStore';
import { isNotNullOrUndefined } from '@utils/utils.tsx';
import { useOrgPortal } from '@hooks/useOrgPortal.ts';
import { notifyShoppingListChanged } from '@features/shopping-list/shoppingListChanged.ts';

export type ProductInList = {
  listId: number;
  listName: string;
};

type CreateAddProductSLState = {
  loading: boolean;
  adding: boolean;
  error: string | null;

  selectedPriceVariantId: number | null;
  selectedProductId: number | null;

  allShoppingLists: ClientShoppingListSummaryDTO[];
  productInLists: ProductInList[];

  // Actions
  loadShoppingLists: (priceVariantId: number, productId?: number) => Promise<void>;
  addToShoppingList: (listId: number, productId: number) => Promise<void>;
  removeFromShoppingList: (listItemId: number, productId: number) => Promise<void>;
  createShoppingList: (name: string) => Promise<void>;
};

export const useCreateAddProductSLStore = create<CreateAddProductSLState>((set, get) => ({
  loading: false,
  adding: false,
  error: null,
  allShoppingLists: [],
  productInLists: [],
  selectedPriceVariantId: null,
  selectedProductId: null,

  // Actions
  loadShoppingLists: async (priceVariantId: number, productId?: number) => {
    try {
      const userId = useUser.getState().beUser.id;
      const portalId = useOrgPortal.getState().portalId;
      const organizationId = useOrgPortal.getState().orgId;
      const resolvedProductId = productId ?? get().selectedProductId;

      set({ loading: true, error: null, selectedPriceVariantId: priceVariantId, selectedProductId: resolvedProductId ?? null });

      const allShoppingListsResponse = await clientShoppingListsManagementApi.getShoppingLists({ userId, organizationId, portalId });
      const allShoppingLists = allShoppingListsResponse.data.shoppingLists || [];

      const productInLists: ProductInList[] = [];
      if (resolvedProductId) {
        const containingResponse = await clientShoppingListsManagementApi.getShoppingListsContainingProduct({
          organizationId,
          portalId,
          productId: resolvedProductId,
        });
        for (const list of containingResponse.data.shoppingLists || []) {
          productInLists.push({ listId: list.id, listName: list.name });
        }
      }

      set({ loading: false, allShoppingLists, productInLists });
    } catch (error) {
      console.error('Error loading shopping lists:', error);
      set({ loading: false, error: 'Failed to load shopping lists' });
    }
  },
  removeFromShoppingList: async (listItemId: number, productId: number) => {
    try {
      const portalId = useOrgPortal.getState().portalId;
      const organizationId = useOrgPortal.getState().orgId;

      await clientShoppingListsManagementApi.removeFromShoppingList({
        portalId,
        organizationId,
        productId,
        shoppingListId: listItemId,
      });
      const sel = get().selectedPriceVariantId;
      if (isNotNullOrUndefined(sel)) {
        await get().loadShoppingLists(sel);
      }
      notifyShoppingListChanged({ shoppingListId: listItemId, productId, action: 'removed' });
    } catch (error) {
      console.error('Error removing item from shopping list:', error);
      set({ error: 'Failed to remove item from shopping list' });

      useToastStore.getState().addToast({
        severity: 'error',
        summary: 'Chyba',
        detail: 'Nepodařilo se odebrat produkt ze seznamu',
      });
    }
  },
  addToShoppingList: async (listId: number, productId: number) => {
    const portalId = useOrgPortal.getState().portalId;
    const organizationId = useOrgPortal.getState().orgId;

    await clientShoppingListsManagementApi.addProductToShoppingList({
      shoppingListId: listId,
      portalId,
      organizationId,
      clientShoppingListAddProductRequest: { productId },
    });
    const sel = get().selectedPriceVariantId;

    if (isNotNullOrUndefined(sel)) {
      await get().loadShoppingLists(sel);
    }
    notifyShoppingListChanged({ shoppingListId: listId, productId, action: 'added' });
  },
  createShoppingList: async (name: string) => {
    try {
      const userId = useUser.getState().beUser.id;
      const portalId = useOrgPortal.getState().portalId;
      const organizationId = useOrgPortal.getState().orgId;

      set({ loading: true, error: null, adding: true });
      await clientShoppingListsManagementApi.createShoppingList({
        userId,
        organizationId,
        portalId,
        createClientShoppingListRequestDTO: { name, products: [] },
      });

      // Znovu načteme seznamy
      const allShoppingListsResponse = await clientShoppingListsManagementApi.getShoppingLists({ userId, organizationId, portalId });
      set({ loading: false, allShoppingLists: allShoppingListsResponse.data.shoppingLists || [], adding: false });

      useToastStore.getState().addToast({
        severity: 'success',
        summary: 'Seznam vytvořen',
        detail: `Nákupní seznam "${name}" byl úspěšně vytvořen`,
      });
    } catch (error) {
      console.error('Error creating shopping list:', error);
      set({ loading: false, error: 'Failed to create shopping list' });

      useToastStore.getState().addToast({
        severity: 'error',
        summary: 'Chyba',
        detail: 'Nepodařilo se vytvořit nákupní seznam',
      });
    }
  },
}));
